export const BUDGET_VIEW_MODE_BASIC = "basic";
const BUDGET_VIEW_MODE_ADVANCED = "advanced";
export const ANALYSIS_CHART_METRIC_ALL = "all-expenses";
export const ANALYSIS_CHART_METRIC_CATEGORY = "category-expenses";
export const ANALYSIS_CHART_METRIC_PAYMENT_METHOD = "payment-method-expenses";
export const ANALYSIS_CHART_METRIC_SERVICE = "service-expenses";
export const ANALYSIS_CHART_METRIC_SUPPLIER = "supplier-expenses";
export const ANALYSIS_CHART_METRIC_PROJECTION = "projection";
export const ANALYSIS_BUDGET_REVIEW_FILTER_ALL = "all";
export const ANALYSIS_BUDGET_REVIEW_FILTER_OVER = "over";
export const ANALYSIS_BUDGET_REVIEW_FILTER_WITHIN = "within";
export const PROFILE_CURRENCY_ILS = "ILS";
const PROFILE_CURRENCY_USD = "USD";
const PROFILE_CURRENCY_EUR = "EUR";
const PROFILE_CURRENCY_GBP = "GBP";

const EXPENSE_VISIBILITY_THIS_MONTH = "this-month";
const EXPENSE_VISIBILITY_LAST_MONTH = "last-month";
const EXPENSE_VISIBILITY_THIS_AND_LAST_MONTH = "this-and-last-month";
export const EXPENSE_VISIBILITY_LAST_10 = "last-10";
export const EXPENSE_VISIBILITY_LAST_30 = "last-30";
export const EXPENSE_VISIBILITY_LAST_10_DAYS = "last-10-days";
export const EXPENSE_VISIBILITY_LAST_30_DAYS = "last-30-days";
export const EXPENSE_VISIBILITY_ALL = "all";

const budgetViewModes = new Set([
  BUDGET_VIEW_MODE_BASIC,
  BUDGET_VIEW_MODE_ADVANCED
]);
const analysisExplorerPresets = new Set([
  "month-category-supplier",
  "month-type-category-supplier",
  "month-method-category-supplier",
  "category-supplier-month",
  "category-supplier-matrix",
  "category-month-supplier",
  "supplier-month-category",
  "supplier-category-month"
]);
const analysisChartMetrics = new Set([
  ANALYSIS_CHART_METRIC_ALL,
  ANALYSIS_CHART_METRIC_CATEGORY,
  ANALYSIS_CHART_METRIC_PAYMENT_METHOD,
  ANALYSIS_CHART_METRIC_SERVICE,
  ANALYSIS_CHART_METRIC_SUPPLIER,
  ANALYSIS_CHART_METRIC_PROJECTION
]);
const analysisBudgetReviewFilters = new Set([
  ANALYSIS_BUDGET_REVIEW_FILTER_ALL,
  ANALYSIS_BUDGET_REVIEW_FILTER_OVER,
  ANALYSIS_BUDGET_REVIEW_FILTER_WITHIN
]);

const profileCurrencies = new Set([
  PROFILE_CURRENCY_ILS,
  PROFILE_CURRENCY_USD,
  PROFILE_CURRENCY_EUR,
  PROFILE_CURRENCY_GBP
]);

const expenseVisibilityFilters = new Set([
  EXPENSE_VISIBILITY_THIS_MONTH,
  EXPENSE_VISIBILITY_LAST_MONTH,
  EXPENSE_VISIBILITY_THIS_AND_LAST_MONTH,
  EXPENSE_VISIBILITY_LAST_10,
  EXPENSE_VISIBILITY_LAST_30,
  EXPENSE_VISIBILITY_LAST_10_DAYS,
  EXPENSE_VISIBILITY_LAST_30_DAYS,
  EXPENSE_VISIBILITY_ALL
]);

const HOME_DASHBOARD_TILE_COUNT = 12;
const homeDashboardMetricKeys = new Set([
  "time-now",
  "date-today",
  "spent-total-this-month",
  "spent-total-last-month",
  "spent-total-this-year",
  "spent-ongoing-this-month",
  "spent-ongoing-last-month",
  "spent-ongoing-this-year",
  "spent-irregular-this-month",
  "spent-irregular-last-month",
  "spent-irregular-this-year",
  "budget-remaining-this-month",
  "budget-remaining-this-year",
  "budget-total-this-year",
  "projection-projected-spend-this-year",
  "projection-implied-spend-this-month",
  "meter-ongoing",
  "meter-total",
  "chart-expenses",
  "chart-category-expenses",
  "chart-supplier-expenses",
  "chart-service-expenses",
  "chart-payment-method-expenses",
  "category-spent-this-month",
  "category-spent-this-year",
  "supplier-spent-this-month",
  "supplier-spent-this-year",
  "service-spent-this-month",
  "service-spent-this-year",
  "payment-method-spent-this-month",
  "payment-method-spent-this-year",
  "blank"
]);
const defaultHomeDashboardTiles = [
  "time-now",
  "date-today",
  "spent-total-this-month",
  "spent-total-last-month",
  "spent-total-this-year",
  "spent-ongoing-this-month",
  "spent-ongoing-last-month",
  "spent-ongoing-this-year",
  "spent-irregular-this-month",
  "blank",
  "blank",
  "blank"
].map((metricKey) => ({
  metricKey,
  args: {}
}));

export const defaultProfileSettings = {
  currency: PROFILE_CURRENCY_ILS,
  budget: {
    viewMode: BUDGET_VIEW_MODE_ADVANCED
  },
  expenses: {
    visibilityFilter: EXPENSE_VISIBILITY_ALL
  },
  analysis: {
    explorer: {
      preset: "month-category-supplier"
    },
    charts: {
      metric: ANALYSIS_CHART_METRIC_ALL
    },
    budgetReview: {
      filter: ANALYSIS_BUDGET_REVIEW_FILTER_ALL
    }
  },
  home: {
    tiles: defaultHomeDashboardTiles
  }
};

function normalizeBudgetViewMode(value) {
  return budgetViewModes.has(value) ? value : defaultProfileSettings.budget.viewMode;
}

function normalizeExpenseVisibilityFilter(value) {
  return expenseVisibilityFilters.has(value) ? value : defaultProfileSettings.expenses.visibilityFilter;
}

function normalizeProfileCurrency(value) {
  return profileCurrencies.has(value) ? value : defaultProfileSettings.currency;
}

function normalizeAnalysisExplorerPreset(value) {
  const migratedValue = value === "category-supplier"
    ? "category-supplier-month"
    : value === "supplier-month"
      ? "supplier-month-category"
      : value === "supplier-category"
        ? "supplier-category-month"
        : value;
  return analysisExplorerPresets.has(migratedValue) ? migratedValue : defaultProfileSettings.analysis.explorer.preset;
}

function normalizeAnalysisChartsMetric(value) {
  return analysisChartMetrics.has(value) ? value : defaultProfileSettings.analysis.charts.metric;
}

function normalizeAnalysisBudgetReviewFilter(value) {
  return analysisBudgetReviewFilters.has(value)
    ? value
    : defaultProfileSettings.analysis.budgetReview.filter;
}

function normalizeHomeDashboardTile(tile, fallbackTile) {
  const metricKey = homeDashboardMetricKeys.has(tile?.metricKey)
    ? tile.metricKey
    : fallbackTile.metricKey;
  const rawArgs = tile?.args && typeof tile.args === "object" ? tile.args : {};

  return {
    metricKey,
    args: Object.fromEntries(
      Object.entries(rawArgs)
        .filter(([, value]) => value !== null && value !== undefined)
        .map(([key, value]) => [key, String(value)])
    )
  };
}

function normalizeHomeDashboardTiles(value) {
  const tiles = Array.isArray(value) ? value : [];

  return Array.from({ length: HOME_DASHBOARD_TILE_COUNT }, (_, index) => (
    normalizeHomeDashboardTile(tiles[index], defaultHomeDashboardTiles[index])
  ));
}

export function normalizeProfileSettings(settings = {}) {
  return {
    currency: normalizeProfileCurrency(settings?.currency),
    budget: {
      viewMode: normalizeBudgetViewMode(settings?.budget?.viewMode)
    },
    expenses: {
      visibilityFilter: normalizeExpenseVisibilityFilter(settings?.expenses?.visibilityFilter)
    },
    analysis: {
      explorer: {
        preset: normalizeAnalysisExplorerPreset(settings?.analysis?.explorer?.preset)
      },
      charts: {
        metric: normalizeAnalysisChartsMetric(settings?.analysis?.charts?.metric)
      },
      budgetReview: {
        filter: normalizeAnalysisBudgetReviewFilter(settings?.analysis?.budgetReview?.filter)
      }
    },
    home: {
      tiles: normalizeHomeDashboardTiles(settings?.home?.tiles)
    }
  };
}

export function mergeProfileSettings(currentSettings = {}, nextSettings = {}) {
  const current = normalizeProfileSettings(currentSettings);

  return normalizeProfileSettings({
    currency: nextSettings?.currency ?? current.currency,
    budget: {
      ...current.budget,
      ...nextSettings?.budget
    },
    expenses: {
      ...current.expenses,
      ...nextSettings?.expenses
    },
    analysis: {
      explorer: {
        ...current.analysis.explorer,
        ...nextSettings?.analysis?.explorer
      },
      charts: {
        ...current.analysis.charts,
        ...nextSettings?.analysis?.charts
      },
      budgetReview: {
        ...current.analysis.budgetReview,
        ...nextSettings?.analysis?.budgetReview
      }
    },
    home: {
      ...current.home,
      ...nextSettings?.home
    }
  });
}
